"use client"

import { useEffect, useMemo, type ReactNode } from "react"
import Link from "next/link"
import Image from "next/image"
import { m } from "framer-motion"
import { expoOut } from "@/lib/motion"
import { cn } from "@/lib/utils"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { EllaTestSection } from "@/components/chat/ella-test-section"
import {
  type TestData,
  TEST_KEY_PREFIX,
  diseasesSubheadingKeyFor,
  explorerDefaultPackageFor,
  getCrossSellSlugs,
  isImagingProductSlug,
  IMAGING_ADDONS,
  BLOOD_ADDONS,
} from "@/data/tests"
import { ComingSoonPill } from "@/components/ui/coming-soon-pill"
import { resolveServiceAvailability } from "@/lib/service-availability"
import { track } from "@/lib/analytics"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { InsuranceSection } from "@/components/ui/insurance-section"
import { InsuranceCoverageCta } from "@/components/marketing/insurance-coverage-cta"
import { QuizCtaBanner } from "@/components/marketing/quiz-cta-banner"
import { ImagingAddonsSection } from "@/components/ui/imaging-addons-section"
import { useCountry } from "@/components/layout/country-provider"
import { baseBloodTest } from "@/lib/base-blood-test"
import { isBookingOpen } from "@/i18n/config"
import { isAllowedInCountry } from "@/lib/country-gates"
import { canLinkArticle } from "./article-link-visibility"
import { TestHeroPrice } from "@/components/ui/test-hero-price"
import { questionCarouselPhoto } from "@/data/question-carousel-photos"

/** Slugs where the hero "see what your insurance covers" CTA appears
 *  above the title: the two main products plus every blood-test add-on. */
const INSURANCE_CTA_SLUGS = new Set<string>([
  "full-body-mri",
  "advanced-blood-test",
  ...BLOOD_ADDONS,
])

// Insights articles linked from the gender-health "questions we could help
// with" cards (by question index). Questions without a clean article match are
// left unlinked.
const OVARIES_QA_ARTICLE_SLUGS: Record<number, string | undefined> = {
  1: "endometriosis-why-the-silent-disease-is-so-loud-for-1-in-10-women",
  2: "hormone-testing-track-energy-sleep-mood-more",
  3: "endometriosis-why-the-silent-disease-is-so-loud-for-1-in-10-women",
  4: "cancer-survival-rates-statistics",
}
const PROSTATE_QA_ARTICLE_SLUGS: Record<number, string | undefined> = {
  1: "prostate-cancer-symptoms-screening-and-early-detection",
  2: "prostate-cancer-symptoms-screening-and-early-detection",
}
// Profile photos for the "Questions we could help with" cards, by question
// index (1-4 = the four cards). Square crops in Vercel Blob; a missing entry
// falls back to the grey placeholder. Prostate photos not sourced yet.
const OVARIES_QA_PHOTOS: Record<number, string | undefined> = {
  1: "/images/gender-health/ovaries-mri-q1.jpg",
  2: "/images/gender-health/ovaries-mri-q2.jpg",
  3: "/images/gender-health/ovaries-mri-q3.jpg",
  4: "/images/gender-health/ovaries-mri-q4.jpg",
}
const PROSTATE_QA_PHOTOS: Record<number, string | undefined> = {
  1: "/images/gender-health/prostate-mri-q1.jpg",
  2: "/images/gender-health/prostate-mri-q2.jpg",
  3: "/images/gender-health/prostate-mri-q3.jpg",
  4: "/images/gender-health/prostate-mri-q4.jpg",
}
// "Three steps to your gender health scan" card images (book / appointment /
// results), by step index 0-2. Square crops in Vercel Blob, per page.
const OVARIES_STEP_PHOTOS = [
  "/images/gender-health/ovaries-mri-step1.jpg",
  "/images/gender-health/ovaries-mri-step2-scanner.jpg",
  "/images/gender-health/ovaries-mri-step3.jpg",
]
const PROSTATE_STEP_PHOTOS = [
  "/images/gender-health/prostate-mri-step1-tablet.jpg",
  "/images/gender-health/prostate-mri-step2.jpg",
  "/images/gender-health/prostate-mri-step3.jpg",
]
// PRIME trial (biparametric vs multiparametric MRI), Ng et al. JAMA 2025 —
// linked inline from the prostate "bi-parametric accuracy" answer (A3). Uses
// the freely-accessible PubMed record (matches MriProtocolSection).
const PRIME_TRIAL_URL = "https://pubmed.ncbi.nlm.nih.gov/40928788/"

/** Render a test title with a trailing "(…)" parenthetical on its own line —
 *  e.g. the pelvic H1 "Pelvic MRI scan (uterus & ovaries)". Locale-agnostic
 *  (splits on the first " (", which every locale's title carries); a no-op for
 *  titles without a parenthetical, like the prostate one. */
function titleWithParenBreak(title: string) {
  const i = title.indexOf(" (")
  if (i === -1) return title
  return (
    <>
      {title.slice(0, i)}
      <span className="block">{title.slice(i + 1)}</span>
    </>
  )
}

import { ImpactAreaIcon, SmartphoneIcon } from "@/components/ui/icons"
import { QuickFactsSection } from "@/components/ui/quick-facts-section"
import { MriFindsCards } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-mri-finds"
import { MriDoctors } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-mri-doctors"
import { ReviewedByPhysiciansIcon } from "@/components/ui/reviewed-by-physicians-icon"
import { BiomarkerDiseaseExplorer } from "@/components/ui/biomarker-disease-explorer"
import { FaqSection } from "@/components/ui/faq-section"
import { ServicesCarousel } from "@/components/ui/services-carousel"
import { useTranslatedServices } from "@/lib/use-translated-services"
import { applyServicesConfig } from "@/lib/services-overlay"
import { QuestionsHelpSection } from "@/components/ui/questions-help-section"
import { TestimonialsSection } from "@/app/(frontend)/[country]/[locale]/_components/testimonials-section"
import type { Testimonial, CmsServiceConfig } from "@/lib/cms"
import { Button, ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import { MriPackageCards } from "@/components/ui/mri-package-cards"
import { entryMriTierKey } from "@/data/mri-packages"
import {
  MriHeroPromises,
  MriPlanCardsSplit,
  MriPlanFrom,
  MriPlanHeroPrice,
  ImagingPlanHeroPrice,
  MriPlanScope,
} from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-mri-cards"
import {
  MriPlanImaging,
  PlanSiteClose,
} from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-client"
import type { PricingV5Data } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-page-data"
import {
  RelatedArticles,
  type RelatedArticle,
} from "@/components/ui/related-articles"
import { CompareMriPackagesDropdown } from "@/components/ui/compare-mri-packages"
import { BloodTestCard } from "@/components/ui/blood-test-card"
import { ReportMockupIpad } from "@/components/ui/report-mockup-ipad"
import { FindingsLegend } from "@/components/ui/findings-legend"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import { useNewTestsPagesEnabled } from "@/lib/use-new-tests-pages"
import { blobMediaUrl } from "@/lib/blob-url"
import { ScanUspScrollytelling } from "@/components/ui/scan-usp-scrollytelling"
import { VisitWalkthroughTimeline } from "@/components/ui/visit-walkthrough-timeline"
import { MriCredibility } from "@/components/ui/mri-credibility"
import { ProstateMriSection } from "./prostate-mri-section"
import { OvariesMriSection } from "./ovaries-mri-section"
import { MriProtocolSection } from "@/components/ui/mri-protocol-section"
import type { TestHeroOverride } from "./test-hero-override"
import type { TestDetailTracking } from "./test-detail-tracking"
import type { TestDetailLayout } from "./test-detail-client-classic"
import { MriPlanCompare } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-mri-compare"
import {
  MriClose,
  MriCompare,
  MriCouples,
  MriLandingSteps,
  MriOfferSection,
} from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-mri-landing"

/* ───────────────────── Shared data ───────────────────── */

/* ───────────────────── Main component ───────────────────── */

/** Map test slug → biomarker page filter param for "see all biomarkers" link.
 *  Imaging add-ons (mamma-mri, calcium-score, body-composition, brain-volumetry)
 *  are intentionally omitted — they don't have dedicated biomarker filters and
 *  linking to /biomarkers/core is misleading. The link is hidden when a test
 *  has no entry here. The full-body MRI links to the market's entry tier
 *  (`entryMriTierKey`), resolved in the component. */
const BIOMARKER_FILTER_BY_TEST: Record<string, string> = {
  "advanced-blood-test": "blood",
  "advanced-blood-panel": "blood",
  "omega-blood-test": "addon-omega",
  "essential-b-vitamins-test": "addon-bvitamins",
  "hormone-blood-test-women": "blood",
  "hormone-blood-test-men": "blood",
}

export function TestDetailClient({
  test,
  heroOverride,
  tracking,
  layout = "default",
  cmsBookingProductId,
  servicesConfig = [],
  showNewTestsPages = false,
  heroLivePrice,
  packagesFromPrice,
  packagesFromPriceBlood,
  mriPlanData,
  membershipFirstSite = false,
  heroLiveMemberChf,
  initialExperts = [],
  initialFaqs = [],
  initialTestimonials = [],
  testimonialsSlot,
  testimonialCardsRowSlot,
  faqSlot,
  visibleArticleSlugs = [],
  relatedArticles = [],
  ellaEligible = false,
  ellaScope = "tests",
  ellaTestName = "",
}: {
  test: TestData
  /** Copy for the hero's title and the text under it, in place of the
   *  test's own. The blood ad pages set it, see `test-hero-override.ts`. */
  heroOverride?: TestHeroOverride
  /** Where the CTAs say they are; the `/go/fullbodymri` ad page passes its
   *  own, see `mri-go-page.tsx`. Defaults to `test-detail-<slug>`. */
  tracking?: TestDetailTracking
  /** `landing` lays the plan site's full-body MRI out for cold traffic, the
   *  `/go/fullbodymri` ad page's order (Eugene, 08.10). */
  layout?: TestDetailLayout
  /** CMS-managed Supabase booking product id (joined by slug). Preferred over
   *  the hardcoded `PRODUCT_IDS[test.bookingProductId]` when present. */
  cmsBookingProductId?: string
  /** test-detail-ella-v1 — mount gate for the under-fold Ella assistant section
   *  (computed page-side: contextual-chat-eligible + not DE). The variant read
   *  itself happens client-side inside the section. */
  ellaEligible?: boolean
  /** Chat routing scope for the Ella section ("fullbodymri" | "tests"). */
  ellaScope?: string
  /** Localized test title, used for the Ella heading + page context. */
  ellaTestName?: string
  /** Live Stripe regular price (CHF) for the hero; falls back to `test.price`
   *  when absent (slug has no price key, or the feed was unavailable). */
  heroLivePrice?: number
  packagesFromPrice?: string
  packagesFromPriceBlood?: string
  /** The membership-first site only: the pricing page's price feed, so the
   *  MRI packages show as the plan's two MRI cards. */
  mriPlanData?: PricingV5Data
  /** The membership-first site (RELEASE_MEMBERSHIP_FIRST): the hero's black
   *  button. */
  membershipFirstSite?: boolean
  /** Live Stripe member price (CHF) for the hero; falls back to
   *  `test.memberPrice` when null. */
  heroLiveMemberChf?: number | null
  /** CMS `products` control plane (per-country status + order), joined by slug
   *  onto the cross-sell carousel. Empty = ungated render + a Sentry alert from
   *  the loader (fail-loud). */
  servicesConfig?: CmsServiceConfig[]
  showNewTestsPages?: boolean
  initialExperts?: ReviewerExpert[]
  initialFaqs?: {
    question: string
    answer: string
    slug?: string
    updatedAt?: string
    pages?: string[]
  }[]
  initialTestimonials?: Testimonial[]
  /** Streamed CMS sections — async server components in `<Suspense>`,
   *  provided by the page so a slow CMS delays only the section. Each takes
   *  precedence over its `initial*` data prop when present. */
  testimonialsSlot?: ReactNode
  /** The review cards, for the plan site's prostate and pelvic MRI under
   *  their packages (Eugene, 01.10). */
  testimonialCardsRowSlot?: ReactNode
  faqSlot?: ReactNode
  /** Slugs of the blog posts that are actually visible in THIS country, from
   *  the same country-filtered CMS fetch the insights listing uses. The
   *  question cards below link to hardcoded article slugs (`data/tests.ts`,
   *  `*_QA_ARTICLE_SLUGS`), and an editor hiding a post per-market
   *  (`hideInCountries`) must not leave a dead link behind — NL hides 29 of 35
   *  posts, which put six soft-404s on the NL MRI pages. Empty = link nothing. */
  visibleArticleSlugs?: string[]
  relatedArticles?: RelatedArticle[]
}) {
  // This component renders the AHD-6064 redesign and is only mounted for the
  // full-body-MRI page (see the slug branch in page.tsx). Every other test
  // renders `TestDetailClientClassic` (the pre-redesign layout) instead.
  const showNewTestsPagesEnabled = useNewTestsPagesEnabled(showNewTestsPages)
  const ut = useTranslations("ui")
  const tt = useTranslations("tests")
  const insightsT = useTranslations("insights")
  const chatT = useTranslations("chat")
  const locale = useLocale()

  const pt = useTranslations("pricing")
  const rpt = useTranslations("report")
  // [TODO: confirm DE pricing] — `test.price` is a CHF figure. On EUR markets
  // we don't yet have authoritative prices, so hide the price line entirely
  // rather than mislabel the CHF number as EUR.
  const { code: country, currency } = useCountry()
  // EUR markets show it only where gated in AND the live feed priced this
  // test, so the hero never renders the static CHF `test.price` as euros.
  const showPrice =
    currency === "CHF" ||
    (isAllowedInCountry("feature:eur-test-hero-price", country) &&
      heroLivePrice != null)
  // full-body MRI is coming-soon in DE (checkup phase) → its CTAs open the
  // waitlist, so they read "Auf die Warteliste" instead of "Check-up buchen".
  // Where MRI is bookable (CH) the label stays the booking copy. AHD-6680.
  const mriCtaLabel = isBookingOpen(country, "mri")
    ? ut("bookCheckup")
    : ut("joinWaitlist")
  // The CTAs already routed to the waitlist where this test isn't sellable
  // yet, but the page said so nowhere — the hero now carries the same
  // coming-soon pill the carousel card and nav item do (2026-07-29).
  // `resolveServiceAvailability` composes the per-service CMS status with the
  // country/category lifecycle, so DE's checkup phase and a waitlist market
  // both light it up without a per-market branch here.
  const testComingSoon = ["coming_soon", "waitlist", "not_available"].includes(
    resolveServiceAvailability({
      country,
      category: isImagingProductSlug(test.slug) ? "mri" : "blood",
      pathOrSlug: `/tests/${test.slug}`,
      servicesConfig,
    })
  )
  // Membership markets (CH + AT): the hero price leads with member /
  // membership pricing. Country-derived, so the prerendered file already
  // matches — no flicker between the one-off and the member price.
  const isMembershipFirst = isAllowedInCountry(
    "feature:membership-first-positioning",
    country
  )
  const allServices = useTranslatedServices({
    showNewTestsPages: showNewTestsPagesEnabled,
  })
  const crossSellSlugs = getCrossSellSlugs(test.slug)
  // Apply the CMS control plane (gate `hidden`, tag `coming_soon` / `waitlist`,
  // per-country order) before narrowing to the cross-sell set, so the carousel
  // pills + ordering come ONLY from CMS. Empty config = ungated passthrough
  // (the loader fires Sentry in that case — fail-loud).
  const translatedServices = useMemo(
    () =>
      applyServicesConfig(allServices, servicesConfig, country).filter((s) =>
        crossSellSlugs.some((slug) => s.path.endsWith(`/${slug}`))
      ),
    [allServices, servicesConfig, country, crossSellSlugs]
  )
  const keyPrefix = TEST_KEY_PREFIX[test.slug] || ""
  const lp = useLocalePath()
  // MRI is sold as tiers (Core/Advanced/Pro) with no single MRI booking
  // product, so the bare booking URL lands on the generic membership/blood
  // entry. Preselect the entry tier (Core): the booking app reads the
  // package's own type and opens the "Full-body MRI" tab with all tiers shown
  // (verified live + ahead-booking-new bookingFlowStore: selectedPackageType =
  // package_types[0]). So every "Book check-up" on this MRI page leads to the
  // MRI packages, not blood tests. (Eugene feedback, 2026-06-24)
  // Austria sells no Core, so there the CMS product (Core's) gives way to the
  // market's entry tier, Advanced.
  const mriEntryTier = entryMriTierKey(country) as "core" | "advanced" | "pro"
  const bookingProductId =
    test.slug === "full-body-mri" && mriEntryTier !== "core"
      ? PRODUCT_IDS[mriEntryTier]
      : (cmsBookingProductId ??
        (test.bookingProductId
          ? PRODUCT_IDS[test.bookingProductId]
          : PRODUCT_IDS.core))
  const trackingLocation = tracking?.location ?? `test-detail-${test.slug}`
  const planTrack = tracking?.plan ?? "test-full-body-mri"
  const articleHrefFor = (slug: string | undefined) =>
    canLinkArticle(slug, country, visibleArticleSlugs)
      ? lp(`/insights/${slug}`)
      : undefined
  const isFullBodyMri = test.slug === "full-body-mri"
  // The `/go/fullbodymri` ad page's order, on the plan site's full-body MRI
  // only, the page it was made for (Eugene, 08.10).
  const landing =
    layout === "landing" &&
    membershipFirstSite &&
    isFullBodyMri &&
    !!mriPlanData
  // Gender-health MRI add-ons (prostate, ovaries) share the redesign shell but
  // render a bespoke section order keyed off `keyPrefix` (prostateMri/ovariesMri).
  const isGenderHealthMri =
    test.slug === "prostate-mri" || test.slug === "pelvic-mri"
  const experts = initialExperts
  const hasLocalDiseases =
    test.diseaseCategories.length > 0 &&
    test.diseaseCategories.some((c) => c.diseases.length > 0)
  const hasBiomarkerConfig = (test.biomarkerPackages?.length ?? 0) > 0

  const testTitle = tt(`${keyPrefix}Title` as never)
  const testBadge = tt(`${keyPrefix}Badge` as never)
  // The plan site's prostate and pelvic MRI: the full-body MRI holds the
  // scan, so their copy says "part of", not "add-on" (Eugene, 01.10).
  const planCopy = (key: string) =>
    mriPlanData && isGenderHealthMri ? `${key}Plan` : key
  const testDescription = tt(planCopy(`${keyPrefix}Desc`) as never)
  const heroTitle = heroOverride
    ? heroOverride.title
    : isGenderHealthMri
      ? titleWithParenBreak(testTitle)
      : testTitle
  const heroDescription = heroOverride?.description ?? testDescription
  // The plan site's hero price: the MRI's "From", or on prostate and
  // pelvic MRI the scan on top of it, as the other scans (Eugene, 01.10).
  const heroPlanPrice = (dark?: boolean) =>
    isGenderHealthMri ? (
      <ImagingPlanHeroPrice
        slug={test.slug}
        name={testTitle}
        fallback={{
          regular: heroLivePrice ?? test.price,
          member: heroLiveMemberChf ?? test.memberPrice ?? undefined,
        }}
        fromLabel={tt("from")}
        dark={dark}
        track={`test-${test.slug}`}
      />
    ) : (
      <MriPlanHeroPrice fromLabel={tt("from")} dark={dark} track={planTrack} />
    )

  // The phone hero fills the screen's height, so on the membership-first
  // site its photo is fetched for the taller side.
  const heroMobileSizes = membershipFirstSite
    ? "max(100vw, 100vh, 600px)"
    : "100vw"

  useEffect(() => {
    track.viewItem(testTitle, testBadge)
  }, [test.slug, testTitle, testBadge])

  const questions = test.questions.map((q, i) => ({
    ...q,
    quote: tt(`${keyPrefix}Q${i + 1}` as never),
    explanation: tt(`${keyPrefix}A${i + 1}` as never),
    // The membership-first carousel links a phrase in the answer.
    articleLinkText:
      membershipFirstSite && tt.has(`${keyPrefix}A${i + 1}LinkText` as never)
        ? tt(`${keyPrefix}A${i + 1}LinkText` as never)
        : undefined,
    biomarkerName: tt(`${keyPrefix}Q${i + 1}Bio` as never),
    biomarkerValue: q.biomarkerValueKey
      ? ut(q.biomarkerValueKey as never)
      : q.biomarkerValue,
    outOfRange: q.biomarkerStatus === "outOfRange",
    // Same status mapping as the classic layout: an optional
    // `<prefix>Q<n>Status` key overrides the generic pill (e.g. PI-RADS 2 is
    // "low likelihood", not a "normal range" — AHD-13183).
    statusLabel: tt.has(`${keyPrefix}Q${i + 1}Status` as never)
      ? tt(`${keyPrefix}Q${i + 1}Status` as never)
      : q.biomarkerStatus === "outOfRange"
        ? ut("outOfRange")
        : q.biomarkerStatus === "observation"
          ? ut("observation")
          : q.biomarkerStatus === "negative"
            ? ut("negative")
            : ut("normalRange"),
    articleHref: articleHrefFor(q.articleSlug),
  }))

  // Health reports section — extracted so it can sit directly under
  // How it works for full-body MRI, while other tests keep it lower down.
  const healthReportsSection = (
    <section className="py-16 md:py-20">
      <div>
        <div className="px-4 md:px-10">
          <h2 className="type-display-md text-center text-neutral-900">
            {tt("reportHeading")}
          </h2>
          <p className="type-body-lg mx-auto mt-4 max-w-[500px] text-center text-neutral-900">
            {tt("reportSub")}
          </p>
          <div className="mt-6 flex justify-center">
            <Link
              href={lp("/report")}
              className={buttonVariants({ variant: "outline" })}
            >
              {ut("exploreReport")}
            </Link>
          </div>
        </div>
      </div>

      {/* iPad report mockup — shared component with hover tooltips */}
      <div>
        <div className="mx-auto mt-12 max-w-6xl px-4 md:px-10">
          <ReportMockupIpad
            explainers={{
              selector: rpt("explainerSelector"),
              nav: rpt("explainerNav"),
              bioAge: rpt("explainerBioAge"),
              heartRisk: rpt("explainerHeartRisk"),
              biomarkers: rpt("explainerBiomarkers"),
              findings: rpt("explainerFindings"),
              summary: rpt("explainerSummary"),
            }}
          />
          <FindingsLegend className="mt-4 justify-center" />
        </div>
      </div>
    </section>
  )

  // ── Sections extracted into consts so the full-body-MRI page can render
  // them in a bespoke order (see the `isFullBodyMri` block in the return),
  // while every other test keeps them in their original positions below. ──
  // The membership-first site shows the questions as its dark carousel,
  // with its own photos and no sub-line.
  const questionsSection = membershipFirstSite ? (
    <QuestionsHelpSection
      heading={tt("questionsHeading")}
      questions={questions.map((question, index) => ({
        ...question,
        photo: questionCarouselPhoto(test.slug, index, question.photo),
      }))}
      learnMoreLabel={ut("learnMore")}
      variant="carousel"
    />
  ) : (
    <QuestionsHelpSection
      heading={tt("questionsHeading")}
      description={tt("questionsSub")}
      questions={questions}
      learnMoreLabel={ut("learnMore")}
      variant={isFullBodyMri ? "feature" : "default"}
    />
  )

  // ── Gender-health MRI bespoke sections (prostate/ovaries) ──
  const genderHealthIntro =
    test.slug === "prostate-mri" ? (
      <ProstateMriSection
        simple={membershipFirstSite}
        ctaLabel={ut("bookCheckup")}
        ctaCategory="mri"
        bookingProductId={bookingProductId}
        trackingLocation={trackingLocation}
      />
    ) : test.slug === "pelvic-mri" ? (
      <OvariesMriSection
        simple={membershipFirstSite}
        ctaLabel={ut("bookCheckup")}
        ctaCategory="mri"
        bookingProductId={bookingProductId}
        trackingLocation={trackingLocation}
      />
    ) : null

  const genderHealthStepPhotos =
    test.slug === "pelvic-mri"
      ? OVARIES_STEP_PHOTOS
      : test.slug === "prostate-mri"
        ? PROSTATE_STEP_PHOTOS
        : undefined
  const genderHealthSteps = [
    {
      title: tt(`${keyPrefix}WalkStep1Title` as never),
      body: tt(`${keyPrefix}WalkStep1Body` as never),
      placeholderLabel: "Booking flow",
      image: genderHealthStepPhotos?.[0],
      // Step 1 is the booking step — surface the primary "Book your scan" CTA
      // plus the insurance-coverage check right under the copy.
      actions: (
        <div className="flex flex-wrap items-center gap-4">
          <BookingCta
            productId={bookingProductId}
            category="mri"
            trackingLabel={tt(`${keyPrefix}WalkStep1Title` as never)}
            trackingLocation={`${trackingLocation}-walkthrough-step1`}
          >
            <Button
              variant="primary"
              className="group"
              iconRight={
                <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
              }
            >
              {isBookingOpen(country, "mri")
                ? tt(`${keyPrefix}WalkStep1Title` as never)
                : ut("joinWaitlist")}
            </Button>
          </BookingCta>
          <InsuranceCoverageCta size="md" icon="heart-check-pulse" />
        </div>
      ),
    },
    {
      title: tt(`${keyPrefix}WalkStep2Title` as never),
      body: tt(`${keyPrefix}WalkStep2Body` as never),
      placeholderLabel: "Inside the scanner",
      image: genderHealthStepPhotos?.[1],
    },
    {
      title: tt(`${keyPrefix}WalkStep3Title` as never),
      body: tt(`${keyPrefix}WalkStep3Body` as never),
      placeholderLabel: "Results review",
      image: genderHealthStepPhotos?.[2],
      // Step 3 is the results step — let people preview the deliverable.
      actions: (
        <Link
          href={lp("/report")}
          className={cn(
            buttonVariants({ variant: "outline" }),
            "h-auto max-w-full py-2.5 text-center whitespace-normal"
          )}
        >
          {ut("viewSampleReport")}
        </Link>
      ),
    },
  ]
  const genderHealthWalkthrough = isGenderHealthMri ? (
    <VisitWalkthroughTimeline
      label={tt(`${keyPrefix}WalkLabel` as never)}
      heading={tt(`${keyPrefix}WalkHeading` as never)}
      stepWord={tt(`${keyPrefix}WalkStepWord` as never)}
      steps={genderHealthSteps}
      plain={membershipFirstSite}
    />
  ) : null

  const genderHealthArticleSlugs =
    test.slug === "pelvic-mri"
      ? OVARIES_QA_ARTICLE_SLUGS
      : test.slug === "prostate-mri"
        ? PROSTATE_QA_ARTICLE_SLUGS
        : undefined
  const genderHealthQuestionPhotos =
    test.slug === "pelvic-mri"
      ? OVARIES_QA_PHOTOS
      : test.slug === "prostate-mri"
        ? PROSTATE_QA_PHOTOS
        : undefined
  const genderHealthQuestionsSection = isGenderHealthMri ? (
    <QuestionsHelpSection
      heading={tt("questionsHeading")}
      description={
        membershipFirstSite ? undefined : tt(`${keyPrefix}HelpSub` as never)
      }
      learnMoreLabel={ut("learnMore")}
      variant={membershipFirstSite ? "carousel" : "feature"}
      questions={[1, 2, 3, 4].map((i) => {
        const slug = genderHealthArticleSlugs?.[i]
        // Prostate A3 carries an inline link to the PRIME trial; everything
        // else is plain text.
        const explanation =
          test.slug === "prostate-mri" && i === 3
            ? (
                tt.rich as unknown as (
                  k: string,
                  v: { link: (c: ReactNode) => ReactNode }
                ) => ReactNode
              )(`${keyPrefix}HelpA${i}`, {
                link: (chunks) => (
                  <a
                    href={PRIME_TRIAL_URL}
                    target="_blank"
                    rel="noreferrer"
                    className={
                      // White on the membership-first carousel's dark card.
                      membershipFirstSite
                        ? "rounded-sm underline decoration-white/40 underline-offset-4 transition-colors hover:text-white hover:decoration-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
                        : "underline underline-offset-2 hover:text-neutral-900"
                    }
                  >
                    {chunks}
                  </a>
                ),
              })
            : tt(`${keyPrefix}HelpA${i}` as never)
        return {
          quote: tt(`${keyPrefix}HelpQ${i}` as never),
          explanation,
          articleHref: articleHrefFor(slug),
          articleLinkText:
            membershipFirstSite &&
            tt.has(`${keyPrefix}HelpA${i}LinkText` as never)
              ? tt(`${keyPrefix}HelpA${i}LinkText` as never)
              : undefined,
          // Per-question profile photo; empty triggers the placeholder fallback.
          photo: membershipFirstSite
            ? questionCarouselPhoto(
                test.slug,
                i - 1,
                genderHealthQuestionPhotos?.[i] ?? ""
              )
            : (genderHealthQuestionPhotos?.[i] ?? ""),
          biomarkerName: "",
          biomarkerValue: "",
          biomarkerStatus: "normal" as const,
        }
      })}
    />
  ) : null

  const diseasesSubheadingKey = diseasesSubheadingKeyFor(test.slug)
  const diseasesSection =
    hasBiomarkerConfig || hasLocalDiseases ? (
      // `#biomarkers` anchor — linked from the gender-health "View biomarkers
      // list" button. scroll-mt clears the sticky navbar.
      <div id="biomarkers" className="scroll-mt-24">
        <BiomarkerDiseaseExplorer
          packages={test.biomarkerPackages ?? []}
          fallbackDiseases={test.diseaseCategories}
          isBloodTest={test.pricingType === "blood"}
          defaultTab={
            BLOOD_ADDONS.includes(test.slug) ? "biomarkers" : undefined
          }
          diseasesTab={
            diseasesSubheadingKey
              ? {
                  subheading: tt(planCopy(diseasesSubheadingKey) as never),
                }
              : undefined
          }
          defaultPackageFilter={explorerDefaultPackageFor(test.slug, country)}
        />
      </div>
    ) : null

  const pricingSection = (
    <>
      <section
        id="preise"
        className={cn(
          "bg-background pt-16 md:pt-20",
          // MRI pages render the compare-packages button below the cards,
          // which adds its own py-8 — so trim the section's bottom padding to
          // avoid a double gap before the next CTA. Blood pages keep the full
          // symmetric padding.
          test.pricingType === "mri" ? "pb-4 md:pb-6" : "pb-16 md:pb-20"
        )}
      >
        <div>
          <div className="px-4 md:px-10">
            {/* Gender-health MRI is an add-on, not a standalone package — call
                that out above the heading and link to the full-body MRI it
                rides on. */}
            {/* Not on the plan site: the full-body MRI holds the scan
                there, as its hero says (Eugene, 01.10). */}
            {isGenderHealthMri && !mriPlanData && (
              <p className="type-body-md mx-auto mb-4 max-w-[500px] text-center text-neutral-600">
                {(
                  tt.rich as unknown as (
                    k: string,
                    v: { link: (c: ReactNode) => ReactNode }
                  ) => ReactNode
                )(`${keyPrefix}PricingAddonNote`, {
                  link: (chunks) => (
                    <Link
                      href={lp("/tests/full-body-mri")}
                      className="underline underline-offset-2 hover:text-neutral-900"
                    >
                      {chunks}
                    </Link>
                  ),
                })}
              </p>
            )}
            <h2 className="type-display-md text-center text-neutral-900">
              {test.pricingType === "blood"
                ? tt("packagesHeadingBlood")
                : tt("packagesHeading")}
            </h2>
            <p className="type-body-lg mx-auto mt-3 max-w-[500px] text-center text-neutral-900">
              {test.pricingType === "blood" ? (
                tt("packagesSubBlood", {
                  price: packagesFromPriceBlood ?? "",
                })
              ) : mriPlanData ? (
                // The plan site: what the scan adds to the plan.
                <MriPlanFrom />
              ) : (
                tt("packagesSub", { price: packagesFromPrice ?? "" })
              )}
            </p>
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-[1440px] px-4 md:px-10">
          {test.pricingType === "mri" && mriPlanData ? (
            // The membership-first site sells the MRI with the plan: the
            // pricing page's two cards, the plan a year plus the scan once
            // (Eugene, 30.09). The same component as /pricing.
            <MriPlanCardsSplit
              track={planTrack}
              className="mx-auto max-w-[1360px]"
            />
          ) : test.pricingType === "mri" ? (
            <MriPackageCards />
          ) : (
            <div className="mx-auto max-w-[960px]">
              <BloodTestCard />
            </div>
          )}
        </div>

        {/* Compare packages dropdown + insurance coverage link — mirrors the
            arrangement under the pricing cards on /pricing. MRI packages only. */}
        {/* The landing, and every MRI page on the plan site: the booking
            app's comparison popup of the plan's scans (Eugene, 08.10 and
            09.10). */}
        {(landing || (test.pricingType === "mri" && mriPlanData)) && (
          <div className="flex flex-wrap items-center justify-center gap-3 px-4 py-8">
            <MriPlanCompare />
            <InsuranceCoverageCta
              size="md"
              icon="heart-check-pulse"
              className="hover:bg-transparent"
            />
          </div>
        )}
        {test.pricingType === "mri" && !landing && !mriPlanData && (
          <CompareMriPackagesDropdown
            secondaryButton={
              // On mobile (stacked) Insurance coverage reads first; md+
              // reverts to source order so Compare sits on the left.
              <InsuranceCoverageCta
                size="md"
                icon="heart-check-pulse"
                className="order-1 hover:bg-transparent md:order-2"
              />
            }
          />
        )}
      </section>

      {mriPlanData ? (
        // The plan site: the scans that go with the MRI in the pricing
        // page's cards, then the quiz (Eugene, 30.09).
        <section
          data-testid="plan-block"
          className="bg-background px-4 pt-4 pb-16 md:px-10 md:pt-8 md:pb-24"
        >
          {/* As wide as the MRI cards and the page's content (Eugene,
              12.10: wider when there is room). */}
          <MriPlanImaging className="max-w-[1360px]" />
          {/* The landing closes on its booking, not the quiz. */}
          {!landing && (
            <div className="mx-auto mt-16 max-w-[960px] md:mt-24">
              <QuizCtaBanner variant="compact" />
            </div>
          )}
        </section>
      ) : (
        <>
          <section className="bg-background">
            <div className="mx-auto max-w-[1440px] px-4 md:px-10">
              <QuizCtaBanner variant="compact" />
            </div>
          </section>

          {/* Optional imaging add-ons (shared with /pricing) — sits below the
          "Not sure which check-up?" quiz CTA. On the gender-health pages it's
          retitled "Additional imaging add-ons" and hides the Gender Health
          card (you're already on it). */}
          <ImagingAddonsSection
            servicesConfig={servicesConfig}
            heading={
              isGenderHealthMri ? tt("additionalImagingAddons") : undefined
            }
            excludeTitleKeys={
              isGenderHealthMri ? ["addonGenderHealth"] : undefined
            }
          />
        </>
      )}
    </>
  )

  const insuranceSection = (
    <InsuranceSection
      trackingLocation={tracking?.insurance ?? "test-detail-insurance"}
    />
  )

  // Cross-sell carousel ("Perfekt kombinierbar"): off on AT until more
  // Austrian test pages are back; today it would offer only the analyses
  // bundled into Advanced and Pro.
  // country-gate-ignore: AT content hide, not a page gate
  const hideCrossSell = country === "at"
  const crossSellSection = (
    <section className="py-16 md:py-20">
      <div>
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <h2 className="type-display-sm text-neutral-900">
            {tt("madeForEachOther")}
          </h2>
          <p className="type-body-lg mt-4 max-w-2xl text-neutral-500">
            {tt(`${keyPrefix}MadeForEachOtherSub` as never) ||
              tt("madeForEachOtherSub")}
          </p>
        </div>
      </div>
      <div className="mt-10">
        <ServicesCarousel
          items={translatedServices}
          learnMoreLabel={ut("learnMore")}
          disclaimerLabel={ut("illustrativeSamples")}
        />
      </div>
    </section>
  )

  // Related reading — curated insights articles feeding topical authority into
  // the cluster. Renders nothing when the market/curation leaves it empty.
  const relatedReadingSection = (
    <RelatedArticles
      heading={insightsT("relatedArticles")}
      articles={relatedArticles}
    />
  )

  const testimonialsSection = testimonialsSlot ?? (
    <TestimonialsSection
      cmsTestimonials={initialTestimonials}
      heading={tt("realResults")}
      description={tt("realResultsSub")}
    />
  )

  // test-detail-ella-v1 — assistant section, rendered directly below the hero
  // (one insertion point, shared by both hero variants). The variant gate lives
  // inside <EllaTestSection>.
  const ellaSection = ellaEligible ? (
    <EllaTestSection
      scope={ellaScope}
      country={country}
      locale={locale}
      pageUrl={`/${country}/${locale}/tests/${test.slug}`}
      pageTitle={ellaTestName}
      testName={ellaTestName}
      bookingCta={
        <BookingCta
          productId={bookingProductId}
          category="mri"
          trackingLabel={chatT("section.ctaBook", { testName: ellaTestName })}
          trackingLocation={`${trackingLocation}-ella`}
          className="type-title-sm bg-aubergine-800 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-medium text-white transition-[box-shadow] duration-200 hover:shadow-[inset_0_0_0_999px_rgba(255,255,255,0.1)]"
        >
          {chatT("section.ctaBook", { testName: ellaTestName })}
        </BookingCta>
      }
    />
  ) : null

  const faqSection = faqSlot ?? (
    <FaqSection
      heading={tt("faqHeading")}
      linkLabel={ut("readAllFaqs")}
      linkHref={lp("/faq")}
      items={initialFaqs}
      currentPage={`tests/${test.slug}`}
    />
  )

  const biomarkerFilter =
    test.slug === "full-body-mri"
      ? entryMriTierKey(country)
      : BIOMARKER_FILTER_BY_TEST[test.slug]
  const quickFactsSection = (
    <QuickFactsSection
      facts={test.quickFacts}
      mri={isFullBodyMri}
      simple={membershipFirstSite}
      ctaButtons={
        <div className="mt-8 flex flex-col flex-wrap gap-3 sm:flex-row">
          {/* The plan site: the hero books, so here only the list of
                what the test checks, named by what it finds (Eugene,
                02.10). */}
          {!membershipFirstSite && (
            <BookingCta
              productId={bookingProductId}
              category="mri"
              trackingLabel={ut("bookCheckup")}
              trackingLocation={`${trackingLocation}-quick-facts`}
              className={cn(
                buttonVariants({ variant: "primary" }),
                "w-full sm:w-auto"
              )}
            >
              {mriCtaLabel}
            </BookingCta>
          )}
          {biomarkerFilter && (
            <Link
              href={lp(`/biomarkers/${biomarkerFilter}`)}
              className={cn(
                buttonVariants({ variant: "outline" }),
                "w-full sm:w-auto"
              )}
            >
              {membershipFirstSite
                ? ut("seeEverythingWeCheck")
                : ut("viewBiomarkersList")}
            </Link>
          )}
        </div>
      }
    />
  )

  return (
    <MriPlanScope data={mriPlanData}>
      {/* ═══════ Section 1: Hero ═══════ */}
      {/* Make area behind navbar white — hero bg extends under sticky nav */}
      {/* ═══════ Mobile hero: full-bleed background image ═══════ */}
      <section className="relative -mb-px h-lvh min-h-[600px] overflow-hidden md:hidden">
        <Image
          src={test.heroImage}
          alt={testTitle}
          quality={test.heroImageQuality}
          style={{ objectPosition: test.heroMobileImagePosition }}
          fill
          priority
          sizes={heroMobileSizes}
          className="object-cover"
        />
        {/* Progressive blur on bottom third */}
        <div
          className="absolute inset-0"
          style={{
            maskImage: "linear-gradient(to bottom, transparent 55%, black 85%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 55%, black 85%)",
          }}
        >
          <Image
            src={test.heroImage}
            alt=""
            quality={test.heroImageQuality}
            style={{ objectPosition: test.heroMobileImagePosition }}
            fill
            sizes={heroMobileSizes}
            className="object-cover blur-[4px]"
          />
        </div>
        {/* Overlay — matches homepage mobile (solid 50% + bottom gradient for text) */}
        <div className="absolute inset-0 z-[1] bg-neutral-950/50" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-transparent via-transparent to-black/30" />

        <div className="absolute inset-0 z-[2] flex flex-col justify-end px-6 pt-[120px] pb-12">
          {INSURANCE_CTA_SLUGS.has(test.slug) && (
            // -ml-4 pulls the pill so its icon aligns with the title's
            // first letter rather than being inset by the pill's px-4.
            <div className="mb-4 -ml-4">
              <InsuranceCoverageCta size="md" icon="heart-check-pulse" dark />
            </div>
          )}
          {testComingSoon && (
            <div className="mb-3">
              <ComingSoonPill label={ut("comingSoon")} variant="glass" />
            </div>
          )}
          <h1 className="type-display-lg break-words text-white">
            {heroTitle}
          </h1>

          <p className="type-body-xl mt-4 text-white/85">{heroDescription}</p>

          <div className="mt-8">
            <div className="flex flex-col items-center gap-4">
              {/* The plan site: the price first, then the button (Eugene, 30.09). */}
              {mriPlanData && heroPlanPrice(true)}
              <BookingCta
                productId={bookingProductId}
                category="mri"
                trackingLabel={tracking?.hero?.label ?? ut("bookCheckup")}
                trackingLocation={
                  tracking?.hero?.location ?? `${trackingLocation}-hero-mobile`
                }
                className="type-body-lg text-aubergine-900 inline-flex w-full items-center justify-center rounded-full bg-white px-5 py-3.5 font-medium transition-opacity hover:opacity-90 md:px-8"
              >
                {mriCtaLabel}
              </BookingCta>
              {!mriPlanData && (
                <TestHeroPrice
                  test={test}
                  membershipFirst={isMembershipFirst}
                  showPrice={showPrice}
                  theme="dark"
                  bookingProductId={bookingProductId}
                  trackingLocation={trackingLocation}
                  livePrice={heroLivePrice}
                  liveMemberChf={heroLiveMemberChf}
                />
              )}
              {IMAGING_ADDONS.includes(test.slug) && !mriPlanData && (
                <p className="type-body-sm text-center text-white/60">
                  {pt.rich("imagingAddonsCombinationNote", {
                    mri: (chunks) => (
                      <a
                        href={lp("/tests/full-body-mri")}
                        className="underline underline-offset-2 hover:text-white"
                      >
                        {chunks}
                      </a>
                    ),
                  })}
                </p>
              )}
              {BLOOD_ADDONS.includes(test.slug) && (
                <p className="type-body-sm text-center text-white/60">
                  {pt.rich("bloodAddonsCombinationNote", {
                    blood: (chunks) => (
                      <a
                        href={lp(`/tests/${baseBloodTest(country).slug}`)}
                        className="underline underline-offset-2 hover:text-white"
                      >
                        {chunks}
                      </a>
                    ),
                  })}
                </p>
              )}
            </div>

            {mriPlanData ? (
              // The plan site's full-body MRI: the MRI cards' promises (Eugene,
              // 30.09). Prostate and pelvic MRI keep theirs.
              <MriHeroPromises tone="light" className="mt-8 w-full text-left" />
            ) : (
              <m.div
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: {},
                  visible: {
                    transition: { delayChildren: 0.4, staggerChildren: 0.08 },
                  },
                }}
                className="mt-8 grid grid-cols-3 items-start gap-2"
              >
                {[
                  {
                    Icon: ReviewedByPhysiciansIcon,
                    label: pt("trustReviewedByPhysicians"),
                  },
                  {
                    Icon: ImpactAreaIcon,
                    label: pt("trustPersonalActionPlan"),
                  },
                  { Icon: SmartphoneIcon, label: pt("trustInteractiveReport") },
                ].map(({ Icon, label }) => (
                  <m.div
                    key={label}
                    variants={{
                      hidden: { opacity: 0, y: 8 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.5,
                          ease: expoOut as unknown as number[],
                        },
                      },
                    }}
                    className="flex min-w-0 flex-col items-center gap-1"
                  >
                    <Icon size={24} className="text-white/70" />
                    <p className="text-center text-xs font-medium text-white/70">
                      {label}
                    </p>
                  </m.div>
                ))}
              </m.div>
            )}
          </div>
        </div>
      </section>

      {/* ═══════ Desktop hero: two-column grid ═══════ */}
      <div className="relative -mt-[120px] hidden bg-white pt-[120px] md:block">
        {/* Subtle top wash: body-grey fading to white behind the navbar,
            matching the /locations hero gradient. */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-[260px]"
          style={{
            background:
              "linear-gradient(to bottom, #f4f4f4 20%, transparent 100%)",
          }}
        />
        <section className="relative z-[2] pt-16 pb-16">
          <div className="mx-auto grid max-w-[1440px] grid-cols-2 items-center gap-8 px-4 md:px-10">
            {/* Left column — `min-w-0` so a long unbreakable word
                (e.g. German "Körperzusammensetzung") can wrap inside its
                grid column instead of overflowing into the image. */}
            <div className="min-w-0">
              {INSURANCE_CTA_SLUGS.has(test.slug) && (
                // -ml-4 pulls the pill so its icon aligns with the title's
                // first letter rather than being inset by the pill's px-4.
                <div className="mb-4 -ml-4">
                  <InsuranceCoverageCta size="md" icon="heart-check-pulse" />
                </div>
              )}
              {/* Desktop hero heading — ARIA role instead of native <h1> so
                  Lighthouse's one-h1-per-page audit stays green. The native
                  <h1> lives in the mobile hero above; that hero is
                  display:none at md+ so its <h1> is out of the accessibility
                  tree here, and screen readers at this breakpoint navigate
                  to this element as the page's heading level 1. */}
              {testComingSoon && (
                <div className="mb-3">
                  <ComingSoonPill label={ut("comingSoon")} />
                </div>
              )}
              <p
                className="type-display-lg break-words text-neutral-900"
                role="heading"
                aria-level={1}
              >
                {heroTitle}
              </p>

              <p className="type-body-xl mt-4 max-w-lg text-neutral-900">
                {heroDescription}
              </p>

              <div
                className={
                  // The plan site: the price, then the button under it, with
                  // more air above the price (Eugene, 30.09 and 01.10).
                  mriPlanData
                    ? "mt-8 flex flex-col items-start gap-5"
                    : "mt-6 flex items-center gap-4"
                }
              >
                {/* The plan site: the price first, then the button (Eugene, 30.09). */}
                {mriPlanData && heroPlanPrice()}
                <BookingCta
                  productId={bookingProductId}
                  category="mri"
                  trackingLabel={tracking?.hero?.label ?? ut("bookCheckup")}
                  trackingLocation={
                    tracking?.hero?.location ??
                    `${trackingLocation}-hero-desktop`
                  }
                >
                  <Button
                    variant="primary"
                    // The plan site's big black button (Eugene, 30.09).
                    size={membershipFirstSite ? "lg" : undefined}
                    className={cn(
                      "group",
                      membershipFirstSite &&
                        "bg-neutral-950 focus-visible:ring-neutral-400",
                      mriPlanData && "min-w-[280px]"
                    )}
                    iconRight={
                      <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                    }
                  >
                    {mriCtaLabel}
                  </Button>
                </BookingCta>
                {!mriPlanData && (
                  <TestHeroPrice
                    test={test}
                    membershipFirst={isMembershipFirst}
                    showPrice={showPrice}
                    theme="light"
                    bookingProductId={bookingProductId}
                    trackingLocation={trackingLocation}
                    livePrice={heroLivePrice}
                    liveMemberChf={heroLiveMemberChf}
                  />
                )}
              </div>
              {IMAGING_ADDONS.includes(test.slug) && !mriPlanData && (
                <p className="type-body-sm mt-4 text-neutral-500">
                  {pt.rich("imagingAddonsCombinationNote", {
                    mri: (chunks) => (
                      <a
                        href={lp("/tests/full-body-mri")}
                        className="underline underline-offset-2 hover:text-neutral-700"
                      >
                        {chunks}
                      </a>
                    ),
                  })}
                </p>
              )}
              {BLOOD_ADDONS.includes(test.slug) && (
                <p className="type-body-sm mt-4 text-neutral-500">
                  {pt.rich("bloodAddonsCombinationNote", {
                    blood: (chunks) => (
                      <a
                        href={lp(`/tests/${baseBloodTest(country).slug}`)}
                        className="underline underline-offset-2 hover:text-neutral-700"
                      >
                        {chunks}
                      </a>
                    ),
                  })}
                </p>
              )}

              {mriPlanData ? (
                // The plan site's full-body MRI: the MRI cards' promises (Eugene,
                // 30.09). Prostate and pelvic MRI keep theirs.
                <MriHeroPromises className="mt-8 gap-6 [&>li]:w-[140px] [&>li]:flex-none" />
              ) : (
                <m.div
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: {},
                    visible: {
                      transition: { delayChildren: 0.4, staggerChildren: 0.08 },
                    },
                  }}
                  className="mt-8 flex items-start gap-4"
                >
                  {[
                    {
                      Icon: ReviewedByPhysiciansIcon,
                      size: 28,
                      label: pt("trustReviewedByPhysicians"),
                    },
                    {
                      Icon: ImpactAreaIcon,
                      size: 28,
                      label: pt("trustPersonalActionPlan"),
                    },
                    {
                      Icon: SmartphoneIcon,
                      size: 24,
                      label: pt("trustInteractiveReport"),
                    },
                  ].map(({ Icon, size, label }) => (
                    <m.div
                      key={label}
                      variants={{
                        hidden: { opacity: 0, y: 8 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.5,
                            ease: expoOut as unknown as number[],
                          },
                        },
                      }}
                      className="flex w-[130px] flex-col items-center gap-1"
                    >
                      <div className="flex size-10 items-center justify-center">
                        <Icon size={size} className="text-neutral-500" />
                      </div>
                      <p className="type-title-sm text-center text-xs leading-4 font-medium text-neutral-900">
                        {label}
                      </p>
                    </m.div>
                  ))}
                </m.div>
              )}
            </div>

            {/* Right column: hero image with floating cards inside */}
            <div className="relative">
              <div className="relative aspect-[1/1] w-full overflow-hidden rounded-[12px]">
                <Image
                  src={test.heroImage}
                  alt={testTitle}
                  quality={test.heroImageQuality}
                  fill
                  priority
                  sizes="500px"
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/10" />

                {questions.length > 0 && (
                  <m.div
                    initial={{ opacity: 0, y: 12, x: -8 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: expoOut as unknown as number[],
                      delay: 0.5,
                    }}
                    className="absolute bottom-4 left-4 z-10 hidden max-w-[220px] rounded-[12px] bg-white/70 p-[18px] backdrop-blur-[10px] lg:block"
                  >
                    <p className="type-body-lg font-medium text-black">
                      {questions[0].biomarkerName}
                    </p>
                    <span className="type-title-lg mt-0.5 block font-medium text-black">
                      {questions[0].biomarkerValue}
                    </span>
                    <span
                      className={cn(
                        "type-label-md mt-1 inline-block rounded-[37px] px-[9px] py-[5px] font-medium text-neutral-900",
                        questions[0].outOfRange
                          ? "bg-terracotta-100"
                          : "bg-[rgba(184,199,101,0.52)]"
                      )}
                    >
                      {" "}
                      {/* ds-color-ignore: translucent overlay (alpha) */}
                      {questions[0].statusLabel}
                    </span>
                  </m.div>
                )}

                {questions.length > 1 && !questions[1].hideInHero && (
                  <m.div
                    initial={{ opacity: 0, y: -12, x: 8 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    transition={{
                      duration: 0.6,
                      ease: expoOut as unknown as number[],
                      delay: 0.7,
                    }}
                    className="absolute top-4 right-4 z-10 hidden max-w-[220px] rounded-[12px] bg-white/70 p-[18px] backdrop-blur-[10px] lg:block"
                  >
                    <p className="type-body-lg font-medium text-black">
                      {questions[1].biomarkerName}
                    </p>
                    <span className="type-title-lg mt-0.5 block font-medium text-black">
                      {questions[1].biomarkerValue}
                    </span>
                    <span
                      className={cn(
                        "type-label-md mt-1 inline-block rounded-[37px] px-[9px] py-[5px] font-medium text-neutral-900",
                        questions[1].outOfRange
                          ? "bg-terracotta-100"
                          : "bg-[rgba(184,199,101,0.52)]"
                      )}
                    >
                      {" "}
                      {/* ds-color-ignore: translucent overlay (alpha) */}
                      {questions[1].statusLabel}
                    </span>
                  </m.div>
                )}

                {/* Ovaries MRI bespoke result chips (no data-driven questions
                    for this slug). Each key is "Label · STATUS". */}
                {test.slug === "pelvic-mri" &&
                  (
                    [
                      { key: "ovariesMriHeroChip1", pos: "bottom-4 left-4" },
                      { key: "ovariesMriHeroChip2", pos: "top-4 right-4" },
                    ] as const
                  ).map(({ key, pos }) => {
                    const [chipLabel, chipStatus] = tt(key as never).split(
                      " · "
                    )
                    return (
                      <div
                        key={key}
                        className={cn(
                          "absolute z-10 hidden max-w-[240px] rounded-[12px] bg-white/70 p-[18px] backdrop-blur-[10px] lg:block",
                          pos
                        )}
                      >
                        <p className="type-body-lg font-medium text-black">
                          {chipLabel}
                        </p>
                        {chipStatus && (
                          <span className="type-label-md mt-1 inline-block rounded-[37px] bg-[rgba(184,199,101,0.52)] px-[9px] py-[5px] font-medium text-neutral-900">
                            {chipStatus}
                          </span>
                        )}
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* test-detail-ella-v1 — assistant section, directly under the hero. */}
      {ellaSection}

      {/* The plan site's full-body MRI opens with what the scan can show,
          right under the hero (Eugene, 02.10). */}
      {membershipFirstSite && isFullBodyMri && <MriFindsCards />}
      {landing && (
        <MriOfferSection trackingLocation={`${trackingLocation}-offer`} />
      )}

      {/* ═══════ Section 4 (moved up): Quick facts — V06 table layout ═══════ */}
      {/* Shared with the classic detail page via <QuickFactsSection>. Full-body
          MRI gets the benefits-checklist left rail (`mri`); every other test
          keeps the single summary heading from the label-less quick fact. */}
      {!landing && quickFactsSection}

      {/* ═══════ Full-body MRI — bespoke section order ═══════ */}
      {/* Why our scan → Questions → How it works → Our expertise → Pricing →
          Insurance → Health reports → Diseases → Testimonials → Made for each
          other. FAQ + footer CTA follow further down (shared, ungated). The
          shared sections below are gated `!isFullBodyMri` so they don't
          double-render here. */}
      {/* The landing: why the scan, proof, how it goes, what it finds and
          who reads it, then the plan and the price, as /go/bloodtest. No
          scroll USP, health reports, cross-sell or articles. */}
      {landing && (
        <>
          {questionsSection}
          <MriCompare trackingLocation={`${trackingLocation}-compare`} />
          {testimonialCardsRowSlot}
          <MriLandingSteps trackingLocation={`${trackingLocation}-steps`} />
          {diseasesSection}
          <MriDoctors experts={experts} />
          {pricingSection}
          {quickFactsSection}
          <MriCouples trackingLocation={`${trackingLocation}-couples`} />
          {insuranceSection}
        </>
      )}

      {isFullBodyMri && !landing && (
        <>
          {/* The plan site: packages and scans third, right after the quick
              facts (Eugene, 30.09). */}
          {mriPlanData && pricingSection}
          <ScanUspScrollytelling />
          {questionsSection}
          {/* The plan site: the ad page's four steps side by side, not the
              five-step scroll walkthrough (Eugene, 09.10). */}
          {membershipFirstSite ? (
            <MriLandingSteps trackingLocation={`${trackingLocation}-steps`} />
          ) : (
            <VisitWalkthroughTimeline />
          )}
          {/* Soft grey→white bridge so the white Expertise section fades in
              from the grey How-it-works surround above it. */}
          {/* The plan site shows the radiologists themselves, the homepage's
              doctors block turned to the scan (Eugene, 02.10). */}
          {membershipFirstSite ? (
            <MriDoctors experts={experts} />
          ) : (
            <>
              <div
                aria-hidden
                className="from-background h-10 bg-gradient-to-b to-white md:h-12"
              />
              <MriCredibility experts={experts} />
            </>
          )}
          {!mriPlanData && pricingSection}
          {insuranceSection}
          {healthReportsSection}
          {diseasesSection}
          {testimonialsSection}
          {!hideCrossSell && crossSellSection}
          {relatedReadingSection}
        </>
      )}

      {/* ═══════ Gender-health MRI — bespoke section order ═══════ */}
      {/* Bespoke intro (replaces the quick-facts grid, which is skipped via
          empty test.quickFacts) → 3-step cards → feature Q&A → pricing (with
          add-on note) → insurance → reports → diseases (#biomarkers) →
          testimonials → made for each other. FAQ + CTA follow (shared). */}
      {isGenderHealthMri && (
        <>
          {genderHealthIntro}
          {/* The plan site: the packages and scans right after the intro,
              then the reviews, as on the other scans' pages (Eugene,
              01.10). */}
          {mriPlanData && pricingSection}
          {mriPlanData && testimonialCardsRowSlot}
          <MriProtocolSection keyPrefix={keyPrefix} />
          {genderHealthWalkthrough}
          {genderHealthQuestionsSection}
          {!mriPlanData && pricingSection}
          {insuranceSection}
          {healthReportsSection}
          {diseasesSection}
          {!mriPlanData && testimonialsSection}
          {!hideCrossSell && crossSellSection}
        </>
      )}

      {/* ═══════ Section 15: FAQ ═══════ */}
      {/*
        currentPage="tests/<slug>" matches the CMS pages-tag values like
        "tests/full-body-mri" — so test-specific FAQs (tagged to this exact
        test) emit FAQPage schema here. Generic FAQs that surface on this
        page but are canonical elsewhere (e.g. tagged "homepage") render
        visibly without contributing to this page's schema.
      */}
      {/* The landing's FAQ sits on white, as /go/bloodtest's. */}
      {landing ? <div className="bg-white">{faqSection}</div> : faqSection}

      {/* ═══════ Section 16: CTA banner (full-width, page footer) ═══════ */}
      {/* The plan site closes with the homepage's quiz block instead
          (Eugene, 01.10). */}
      {landing ? (
        <MriClose trackingLocation={tracking?.close ?? trackingLocation} />
      ) : membershipFirstSite ? (
        <PlanSiteClose
          scan={isImagingProductSlug(test.slug)}
          productId={bookingProductId}
        />
      ) : (
        <section>
          <div className="relative min-h-[400px] overflow-hidden md:min-h-[500px]">
            <Image
              src={blobMediaUrl("/images/tests/cta-window.jpeg")}
              alt=""
              fill
              quality={90}
              sizes="100vw"
              className="object-cover object-top"
            />
            <div className="absolute inset-0 flex items-center justify-center p-6 md:p-12">
              <div className="w-full max-w-[520px] rounded-[12px] bg-black/35 p-6 text-center backdrop-blur-[11px] md:p-10">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="80"
                  height="24"
                  fill="none"
                  viewBox="0 0 124 35"
                  className="mx-auto h-6 w-auto text-white"
                  role="img"
                  aria-label="Ahead Health"
                >
                  <path
                    fill="currentColor"
                    d="M14.34 3.216 2.4 32.158h2.945l3.357-8.27h14.303l3.357 8.27h3.11L17.533 3.216H14.34Zm-4.642 18.15 6.176-15.174 6.138 15.173H9.698Zm32.395-11.123c-3.4 0-5.887 1.738-7.089 4.052V2.386H32.27v29.771h2.735V19.796c0-4.714 3.026-7.153 6.344-7.153 3.318 0 5.722 2.108 5.722 6.326V32.16h2.736V18.64c0-5.707-3.151-8.394-7.71-8.394l-.003-.002Zm21.541.002c-5.264 0-10.197 4.052-10.197 11.04 0 6.99 4.56 11.33 10.406 11.33 4.52 0 8.5-2.314 9.743-6.574h-2.735c-1.202 2.77-3.69 4.219-6.924 4.219-4.311 0-7.504-3.432-7.71-8.23h17.495v-.745c0-7.236-4.727-11.04-10.075-11.04h-.003Zm-7.42 9.55c.332-4.508 3.608-7.236 7.379-7.236 3.77 0 7.088 2.728 7.297 7.236H56.214Zm62.651-17.409v12.487c-1.16-2.647-4.063-4.63-7.752-4.63-5.265 0-9.908 4.135-9.908 11.04 0 6.906 4.518 11.33 9.908 11.33 3.689 0 6.634-2.274 7.752-5.044v4.588h2.735V2.387h-2.735Zm-7.381 27.87c-4.228 0-7.462-3.433-7.462-8.973 0-5.54 3.234-8.682 7.462-8.682 3.773 0 7.42 2.77 7.42 8.682 0 5.913-3.689 8.972-7.42 8.972ZM92.677 10.477H77.15v2.737l15.418-.38a2.445 2.445 0 0 1 2.509 2.49l-.335 16.834h2.694V15.225a4.755 4.755 0 0 0-4.76-4.748h.002Z"
                  />
                  <path
                    fill="currentColor"
                    d="M89.47 16.834 76.677 30.626l1.946 1.99L91.49 18.695a1.366 1.366 0 0 0-.094-1.949 1.375 1.375 0 0 0-1.925.089Z"
                  />
                </svg>
                <h2 className="type-headline-md mt-6 text-white">
                  {tt("ctaBannerHeading")}
                </h2>
                <div className="mt-6 flex flex-col justify-center gap-3">
                  <BookingCta
                    productId={bookingProductId}
                    category="mri"
                    trackingLabel={ut("bookCheckup")}
                    trackingLocation={`${trackingLocation}-cta-banner`}
                    className={buttonVariants({ variant: "primary" })}
                  >
                    {mriCtaLabel}
                  </BookingCta>
                  <Link
                    href={lp("/contact")}
                    className={cn(
                      buttonVariants({ variant: "outline" }),
                      "border-white/40 text-white hover:border-white/60 hover:bg-white/10"
                    )}
                  >
                    {ut("talkToTeam")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </MriPlanScope>
  )
}
